import type { Difficulty, IslandDefinition, MapKind, ShipKind, ShipState, Side } from './types';

export const FIXED_STEP = 1 / 60;
export const BROADSIDE_HALF_ARC = Math.PI * 2 / 9;
export const PROJECTILE_SPEED = 52;
export const PROJECTILE_TTL = 2.5;
export const MAX_PROJECTILES = 96;

export interface ShipDefinition {
  maxHp: number; maxSpeed: number; turnRate: number; reload: number; range: number;
  damage: number; cannons: number; radius: number; length: number;
}
export const SHIP_CONFIG: Record<ShipKind, ShipDefinition> = {
  sloop: { maxHp: 110, maxSpeed: 12, turnRate: 1.2, reload: 2.5, range: 86, damage: 10, cannons: 3, radius: 4, length: 14 },
  frigate: { maxHp: 170, maxSpeed: 10, turnRate: 0.94, reload: 3.1, range: 94, damage: 11, cannons: 4, radius: 5, length: 18 },
  galleon: { maxHp: 250, maxSpeed: 8, turnRate: 0.76, reload: 3.8, range: 102, damage: 12, cannons: 6, radius: 6, length: 23 },
};

// Player helm favors immediate arcade control, including when the sails are lowered.
// Keep AI maneuvering at its existing pace so a responsiveness change is not a difficulty increase.
export const STEERING_CONFIG = {
  player: { rateMultiplier: 1.5, stationaryAuthority: 0.92 },
  enemy: { rateMultiplier: 1, stationaryAuthority: 0.6 },
} as const;

export const MAP_CONFIG: Record<MapKind, { arenaRadius: number; islands: IslandDefinition[] }> = {
  open: { arenaRadius: 200, islands: [] },
  islands: { arenaRadius: 200, islands: [
    { x: -50, z: 22, radius: 16, height: 12 },
    { x: 49, z: -35, radius: 20, height: 15 },
    { x: 68, z: 63, radius: 13, height: 9 },
    { x: -77, z: -77, radius: 24, height: 14 },
  ] },
  fortress: { arenaRadius: 200, islands: [
    { x: -50, z: 58, radius: 20, height: 16 },
    { x: 48, z: 63, radius: 20, height: 16 },
    { x: 0, z: -80, radius: 30, height: 22 },
    { x: 110, z: -23, radius: 17, height: 11 },
  ] },
};

export const DIFFICULTY_CONFIG: Record<Difficulty, {
  enemyCount: number; healthScale: number; damageScale: number; reloadScale: number; aimSpread: number; aimAssistAngle: number;
}> = {
  easy: { enemyCount: 1, healthScale: 0.7, damageScale: 0.45, reloadScale: 1.4, aimSpread: 0.07, aimAssistAngle: BROADSIDE_HALF_ARC },
  normal: { enemyCount: 3, healthScale: 0.8, damageScale: 0.65, reloadScale: 1.2, aimSpread: 0.045, aimAssistAngle: Math.PI / 18 },
  hard: { enemyCount: 5, healthScale: 0.9, damageScale: 0.8, reloadScale: 1.1, aimSpread: 0.025, aimAssistAngle: 0 },
};

/** The shortest signed rotation from b to a, in radians. */
export function angleDelta(a: number, b: number): number {
  return Math.atan2(Math.sin(a - b), Math.cos(a - b));
}

/** Heading zero is +z. With up=+y, captain's starboard is forward×up: local −x. */
export function canTarget(ship: ShipState, target: ShipState, side: Side): boolean {
  if (!ship.alive || !target.alive || ship.id === target.id || ship.faction === target.faction) return false;
  const x = target.x - ship.x;
  const z = target.z - ship.z;
  const distance = Math.hypot(x, z);
  if (distance < 0.01 || distance > SHIP_CONFIG[ship.kind].range) return false;
  const bearing = Math.atan2(x, z);
  const broadside = ship.heading + (side === 'starboard' ? -Math.PI / 2 : Math.PI / 2);
  return Math.abs(angleDelta(bearing, broadside)) <= BROADSIDE_HALF_ARC + 1e-10;
}
